import { OutputTemplateKind } from "@prisma/client";

import {
  OUTPUT_TEMPLATES_BASE_PATH,
  outputTemplateEditorPath,
  outputTemplateListPath,
} from "~/modules/output-templates/output-template-routes";

interface Routes {
  accountingReports: string;
  accountingTransactions: (
    orgSlug: string,
    params?: { accountId?: string; startDate?: string; endDate?: string; postedDateRange?: string[] },
  ) => string;
  bills: string;
  billDetail: (billId: string) => string;
  billNew: string;
  customers: string;
  customerDetail: (customerId: string) => string;
  notes: string;
  notesAdminBulk: string;
  noteDetail: (noteId: string) => string;
  opportunities: string;
  orgSwitcher: string;
  orders: string;
  orderDetail: (orderId: string) => string;
  outputTemplateDocuments: string;
  outputTemplateEditor: (templateId: string) => string;
  outputTemplateLabels: string;
  outputTemplates: string;
  paymentDetail: (paymentId: string) => string;
  pricingEngine: string;
  pricingEngineCostBook: string;
  productDetail: (productId: string) => string;
  productLots: (productId: string) => string;
  products: string;
  purchaseOrders: string;
  purchaseOrderDetail: (orderId: string) => string;
  salesPeopleManagement: string;
  superAdminOrgManagement: string;
  superAdminParkedEmails: string;
  superAdminRolesManagement: string;
  superAdminSubledgerView: string;
  suppliers: string;
  supplierDetail: (supplierId: string) => string;
  userManagement: string;
}

export const routes: Routes = {
  accountingReports: "/accounting/reports",
  accountingTransactions: (orgSlug, params) => {
    const base = `/${orgSlug}/accounting/transactions`;
    if (!params) {
      return base;
    }
    const query = new URLSearchParams();

    if (params.accountId) {
      query.set("accountId", params.accountId);
    }

    // Prefer explicit postedDateRange if provided
    if (params.postedDateRange && params.postedDateRange.length > 0) {
      for (const d of params.postedDateRange) {
        if (d) {
          query.append("postedDateRange", d);
        }
      }
    } else {
      // Back-compat: if start/end provided, map to postedDateRange
      if (params.startDate) {
        query.append("postedDateRange", params.startDate);
      }
      if (params.endDate) {
        query.append("postedDateRange", params.endDate);
      }
    }

    return query.toString() ? `${base}?${query.toString()}` : base;
  },
  bills: "/accounting/accounts-payable/bills",
  billDetail: billId => `/accounting/accounts-payable/bills/${billId}`,
  billNew: "/accounting/accounts-payable/bills/new",
  customers: "/customers",
  customerDetail: customerId => `/customers/${customerId}`,
  notes: "/notes",
  notesAdminBulk: "/notes/admin-bulk",
  noteDetail: noteId => `/notes/${noteId}`,
  opportunities: "/opportunities",
  orgSwitcher: "/org-switcher",
  orders: "/orders",
  orderDetail: orderId => `/orders/${orderId}`,
  // Built from the kind<->path mapping so the route table and the in-module navigation cannot drift.
  outputTemplateDocuments: outputTemplateListPath(OutputTemplateKind.DOCUMENT),
  outputTemplateEditor: templateId => outputTemplateEditorPath(templateId),
  outputTemplateLabels: outputTemplateListPath(OutputTemplateKind.LABEL),
  outputTemplates: OUTPUT_TEMPLATES_BASE_PATH,
  paymentDetail: paymentId => `/accounting/accounts-payable/payments/${paymentId}`,
  pricingEngine: "/pricing-engine",
  pricingEngineCostBook: "/pricing-engine/cost-book",
  products: "/products",
  productDetail: productId => `/products/${productId}`,
  productLots: productId => `/products/${productId}/lots`,
  purchaseOrders: "/purchase-orders",
  purchaseOrderDetail: orderId => `/purchase-orders/${orderId}`,
  salesPeopleManagement: "/settings/sales-people",
  superAdminOrgManagement: "/settings/super/org-management",
  superAdminParkedEmails: "/settings/super/parked-emails",
  superAdminRolesManagement: "/settings/super/roles-management",
  superAdminSubledgerView: "/settings/super/subledger-view",
  suppliers: "/suppliers",
  supplierDetail: supplierId => `/suppliers/${supplierId}`,
  userManagement: "/settings/user-management",
};

export const generateUrlForEntity = (id: string, type: string) => {
  let link = "";

  switch (type) {
    case "CUSTOMER":
      link = routes.customerDetail(id);
      break;
    case "PRODUCT":
      link = routes.productDetail(id);
      break;
    case "SUPPLIER":
      link = routes.supplierDetail(id);
      break;
  }

  return link;
};
